import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { User, Filter, X } from 'lucide-react';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import Footer from '../../components/Footer';
import { useConfig } from '../../contexts/ConfigContext';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import { therapistPublicShort } from '../../utils/therapistPublicText';
import './AgregatorPage.css';

/** Цены для карточки: «от» только если реально больше одной разной цены */
const getTherapistPriceDisplay = (therapist) => {
    const prices = [];

    if (therapist.price != null && therapist.price !== '') {
        const base = parseFloat(therapist.price);
        if (!isNaN(base)) prices.push(base);
    }

    if (therapist.topic_prices && typeof therapist.topic_prices === 'object') {
        Object.values(therapist.topic_prices).forEach((p) => {
            const value = parseFloat(p);
            if (!isNaN(value)) prices.push(value);
        });
    }

    if (prices.length === 0) return null;

    const unique = [...new Set(prices)];
    const minPrice = Math.min(...unique);

    return {
        amount: minPrice,
        showFrom: unique.length > 1
    };
};

const AgregatorPage = () => {
    // Общение с родительским окном через postMessage
    useIframeCommunication();
    const { config } = useConfig();
    const magTitleSvgRef = useRef(null);
    const magTitleTextRef = useRef(null);
    const [filters, setFilters] = useState({
        city: '',
        language: '',
        topic: '',
        format: '',
        minPrice: '',
        maxPrice: '',
        needSupervisor: false
    });
    const SUPERVISION_TOPIC = 'Супервизия';

    // Загрузка опций для фильтров (языки и форматы из organizations.js)
    const { data: options } = useQuery('profileOptions', async () => {
        const response = await axios.get(`${API_BASE_URL}/config/profile-options`);
        return response.data;
    }, {
        enabled: true // Всегда загружаем для языков и форматов
    });

    // Загрузка всех психологов для определения диапазона цен и фильтров
    const { data: allTherapists } = useQuery('allTherapists', async () => {
        const response = await axios.get(`${API_BASE_URL}/users/therapists`);
        return response.data;
    });

    // Вычисляем диапазон цен (используем только минимальную цену каждого психолога)
    const priceRange = useMemo(() => {
        if (!allTherapists || allTherapists.length === 0) {
            return { min: 0, max: 10000 };
        }

        let minPrice = Infinity;
        let maxPrice = 0;

        allTherapists.forEach(therapist => {
            // Получаем минимальную цену психолога
            let therapistMinPrice = therapist.price;

            if (therapist.topic_prices && typeof therapist.topic_prices === 'object') {
                const topicPrices = Object.values(therapist.topic_prices).map(p => parseFloat(p)).filter(p => !isNaN(p));
                if (topicPrices.length > 0) {
                    const topicMin = Math.min(...topicPrices);
                    if (therapistMinPrice === null || topicMin < therapistMinPrice) {
                        therapistMinPrice = topicMin;
                    }
                }
            }

            if (therapistMinPrice !== null) {
                minPrice = Math.min(minPrice, therapistMinPrice);
                maxPrice = Math.max(maxPrice, therapistMinPrice);
            }
        });

        // Округляем до ближайших 100
        minPrice = minPrice === Infinity ? 0 : Math.floor(minPrice / 100) * 100;
        maxPrice = maxPrice === 0 ? 10000 : Math.ceil(maxPrice / 100) * 100;

        return { min: minPrice, max: maxPrice };
    }, [allTherapists]);
    const [priceSlider, setPriceSlider] = useState({ min: 0, max: 0 });

    useEffect(() => {
        setPriceSlider({
            min: filters.minPrice || priceRange.min,
            max: filters.maxPrice || priceRange.max
        });
    }, [priceRange.min, priceRange.max]);

    // Загрузка психологов с фильтрами
    const { data: therapistsRaw, isLoading } = useQuery(
        ['therapists', filters],
        async () => {
            const params = new URLSearchParams();
            if (filters.city) params.append('city', filters.city);
            if (filters.language) params.append('language', filters.language);
            if (filters.topic) params.append('topic', filters.topic);
            if (filters.format) params.append('format', filters.format);
            if (filters.minPrice) params.append('min_price', filters.minPrice);
            if (filters.maxPrice) params.append('max_price', filters.maxPrice);

            const response = await axios.get(`${API_BASE_URL}/users/therapists?${params}`);
            return response.data;
        }
    );

    // Фильтруем и сортируем психологов: только с фото, по алфавиту
    const therapists = useMemo(() => {
        if (!therapistsRaw) return [];

        return therapistsRaw
            .filter(therapist => therapist.photo_url && therapist.photo_url.trim() !== '')
            .sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB, 'ru');
            });
    }, [therapistsRaw]);

    // Вычисляем доступные опции для фильтров из отображаемых психологов
    // Города и темы - из отображаемых психологов
    // Языки и форматы - из organizations.js (все доступные, но фильтруем по отображаемым)
    const availableFilterOptions = useMemo(() => {
        if (!therapists || therapists.length === 0) {
            return {
                cities: [],
                languages: options?.languages || [],
                topics: [],
                formats: options?.formats || []
            };
        }

        const cities = new Set();
        const languages = new Set();
        const topics = new Set();
        const formats = new Set();

        therapists.forEach(therapist => {
            if (therapist.city && Array.isArray(therapist.city)) {
                therapist.city.forEach(city => cities.add(city));
            }
            if (therapist.language && Array.isArray(therapist.language)) {
                therapist.language.forEach(lang => languages.add(lang));
            }
            if (therapist.therapy_topics && Array.isArray(therapist.therapy_topics)) {
                therapist.therapy_topics.forEach(topic => topics.add(topic));
            }
            if (therapist.work_format && Array.isArray(therapist.work_format)) {
                therapist.work_format.forEach(format => formats.add(format));
            }
        });

        // Для языков и форматов берем только те, что есть у отображаемых психологов
        // но сортируем по всем доступным из organizations.js
        const allLanguages = options?.languages || [];
        const allFormats = options?.formats || [];

        return {
            cities: Array.from(cities).sort(),
            languages: Array.from(languages).sort((a, b) => {
                const idxA = allLanguages.indexOf(a);
                const idxB = allLanguages.indexOf(b);
                if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            }),
            topics: Array.from(topics).sort(),
            formats: Array.from(formats).sort((a, b) => {
                const idxA = allFormats.indexOf(a);
                const idxB = allFormats.indexOf(b);
                if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            })
        };
    }, [therapists, options]);

    const handleFilterChange = (name, value) => {
        if (name === 'needSupervisor') {
            const checked = Boolean(value);
            setFilters(prev => ({
                ...prev,
                needSupervisor: checked,
                topic: checked ? SUPERVISION_TOPIC : ''
            }));
            return;
        }
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const currentMin = Number(priceSlider.min || priceRange.min);
    const currentMax = Number(priceSlider.max || priceRange.max);

    const handlePriceRangeInput = (type, value) => {
        const numValue = Number(value);
        setPriceSlider((prev) => {
            if (type === 'min') {
                return { ...prev, min: Math.min(numValue, prev.max) };
            }
            return { ...prev, max: Math.max(numValue, prev.min) };
        });
    };

    const applyPriceDraft = () => {
        setFilters(prev => ({ ...prev, minPrice: currentMin, maxPrice: currentMax }));
    };

    const clearFilters = () => {
        setFilters({
            city: '',
            language: '',
            topic: '',
            format: '',
            minPrice: '',
            maxPrice: '',
            needSupervisor: false
        });
        setPriceSlider({ min: priceRange.min, max: priceRange.max });
    };

    const sliderMinPercent = ((currentMin - priceRange.min) / Math.max(priceRange.max - priceRange.min, 1)) * 100;
    const sliderMaxPercent = ((currentMax - priceRange.min) / Math.max(priceRange.max - priceRange.min, 1)) * 100;

    const isMag = config?.code === 'mag';
    const pageTitle = isMag ? 'МАГРЕГАТОР' : 'Наши психологи';

    const syncMagTitleViewBox = useCallback(() => {
        const svg = magTitleSvgRef.current;
        const text = magTitleTextRef.current;
        if (!svg || !text || !isMag) return;
        if (window.matchMedia('(min-width: 769px)').matches) return;

        try {
            const bbox = text.getBBox();
            if (!bbox.width || !bbox.height) return;
            const padX = bbox.width * 0.01;
            const padY = bbox.height * 0.06;
            svg.setAttribute(
                'viewBox',
                `${bbox.x - padX} ${bbox.y - padY} ${bbox.width + padX * 2} ${bbox.height + padY * 2}`
            );
        } catch (_) {
            // getBBox может кидать, если узел ещё не в документе
        }
    }, [isMag, pageTitle]);

    useLayoutEffect(() => {
        if (!isMag) return undefined;

        let cancelled = false;
        const run = () => {
            if (!cancelled) syncMagTitleViewBox();
        };

        run();
        if (document.fonts?.ready) {
            document.fonts.ready.then(run).catch(() => {});
        }

        window.addEventListener('resize', run);
        return () => {
            cancelled = true;
            window.removeEventListener('resize', run);
        };
    }, [isMag, syncMagTitleViewBox]);

    if (isLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="agregator-page">
            <div className={`page-header${isMag ? ' page-header--mag' : ''}`}>
                <h1 className="page-header-title">{pageTitle}</h1>
                {isMag && (
                    <svg
                        ref={magTitleSvgRef}
                        className="mag-title-svg"
                        viewBox="0 0 1000 140"
                        width="100%"
                        role="img"
                        aria-hidden="true"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <text
                            ref={magTitleTextRef}
                            x="500"
                            y="110"
                            textAnchor="middle"
                            className="mag-title-svg-text"
                        >
                            {pageTitle}
                        </text>
                    </svg>
                )}
                {isMag && (
                    <div className="page-header-buttons">
                        <Link
                            to="/public/therapist-matching"
                            className="btn btn-primary"
                        >
                            Оставить заявку на подбор специалиста
                        </Link>
                        <Link
                            to="/public/therapists-terms"
                            className="btn btn-secondary"
                        >
                            Терапевтам и супервизорам (Условия сотрудничества)
                        </Link>
                        <Link to="/public/payment" className="btn btn-secondary">
                            Оплата сессии
                        </Link>
                    </div>
                )}
            </div>


            <div className="filters-section">
                <div className="filters-header">
                    <div className="filters-title">
                        <Filter size={24} />
                        <h3>Фильтры поиска</h3>
                    </div>
                    <button className="btn-clear-filters" onClick={clearFilters}>
                        <X size={18} />
                        Сбросить
                    </button>
                </div>
                <div className="filters-grid">
                    <div className="filter-group filter-city">
                        <label>Город</label>
                        <select
                            value={filters.city}
                            onChange={(e) => handleFilterChange('city', e.target.value)}
                        >
                            <option value="">Все города</option>
                            {availableFilterOptions.cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group filter-language">
                        <label>Язык</label>
                        <select
                            value={filters.language}
                            onChange={(e) => handleFilterChange('language', e.target.value)}
                        >
                            <option value="">Все языки</option>
                            {availableFilterOptions.languages.map(lang => (
                                <option key={lang} value={lang}>{lang}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group filter-topic">
                        <label>Тема</label>
                        <select
                            value={filters.topic}
                            onChange={(e) => handleFilterChange('topic', e.target.value)}
                            disabled={filters.needSupervisor}
                        >
                            <option value="">Все темы</option>
                            {availableFilterOptions.topics.map(topic => (
                                <option key={topic} value={topic}>{topic}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group filter-format">
                        <label>Формат</label>
                        <select
                            value={filters.format}
                            onChange={(e) => handleFilterChange('format', e.target.value)}
                        >
                            <option value="">Все форматы</option>
                            {availableFilterOptions.formats.map(format => (
                                <option key={format} value={format}>{format}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group filter-supervision">
                        <label>Супервизия</label>
                        <label className="checkbox-row">
                            <input
                                type="checkbox"
                                checked={filters.needSupervisor}
                                onChange={(e) => handleFilterChange('needSupervisor', e.target.checked)}
                            />
                            Нужен супервизор
                        </label>
                    </div>

                    <div className="filter-group price-filter-group filter-price">
                        <label>Цена</label>
                        <div className="price-slider-container">
                            <div className="price-slider-shell">
                                <div className="price-slider-track" />
                                <div
                                    className="price-slider-selected"
                                    style={{
                                        left: `calc(9px + (${sliderMinPercent}% * ((100% - 18px) / 100)))`,
                                        width: `calc(${Math.max(sliderMaxPercent - sliderMinPercent, 0)}% * ((100% - 18px) / 100))`
                                    }}
                                />
                                <input
                                    type="range"
                                    min={priceRange.min}
                                    max={priceRange.max}
                                    step="100"
                                    value={currentMin}
                                    onChange={(e) => handlePriceRangeInput('min', e.target.value)}
                                    onMouseUp={applyPriceDraft}
                                    onTouchEnd={applyPriceDraft}
                                    className="price-slider-thumb price-slider-thumb-min"
                                />
                                <input
                                    type="range"
                                    min={priceRange.min}
                                    max={priceRange.max}
                                    step="100"
                                    value={currentMax}
                                    onChange={(e) => handlePriceRangeInput('max', e.target.value)}
                                    onMouseUp={applyPriceDraft}
                                    onTouchEnd={applyPriceDraft}
                                    className="price-slider-thumb price-slider-thumb-max"
                                />
                            </div>
                            <div className="price-slider-value">{currentMin} - {currentMax} ₽</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="therapists-grid">
                {therapists && therapists.length > 0 ? (
                    therapists.map(therapist => {
                        const qualifications = Array.isArray(therapist.qualification)
                            ? therapist.qualification
                            : (therapist.qualification ? [therapist.qualification] : []);
                        const priceDisplay = getTherapistPriceDisplay(therapist);

                        return (
                            <Link
                                key={therapist.id}
                                to={`/public/profile/${therapist.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="therapist-card"
                            >
                                <div className="therapist-photo">
                                    {therapist.photo_url ? (
                                        <img src={therapist.photo_url} alt={formatUserDisplayName(therapist)} />
                                    ) : (
                                        <div className="photo-placeholder">
                                            <User size={48} />
                                        </div>
                                    )}
                                </div>
                                <div className="therapist-info">
                                    <h3>{formatUserDisplayName(therapist)}</h3>
                                    {therapistPublicShort(therapist) && (
                                        <p className="short-description">
                                            {therapistPublicShort(therapist)}
                                        </p>
                                    )}
                                    {qualifications.length > 0 && (
                                        <div className="qualification">
                                            {qualifications.join(', ')}
                                        </div>
                                    )}
                                    {priceDisplay && (
                                        <div className="price">
                                            {priceDisplay.showFrom ? 'от ' : ''}
                                            {priceDisplay.amount} ₽
                                        </div>
                                    )}
                                    <div className="therapist-tags">
                                        {therapist.city && therapist.city.length > 0 && (
                                            <span className="tag">📍 {therapist.city.join(', ')}</span>
                                        )}
                                        {therapist.work_format && therapist.work_format.length > 0 && (
                                            <span className="tag">💼 {therapist.work_format.join(', ')}</span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        );
                    })
                ) : (
                    <div className="empty-state">
                        <User size={64} className="empty-icon" />
                        <h3>Психологи не найдены</h3>
                        <p>Попробуйте изменить параметры фильтрации</p>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default AgregatorPage;


